import { ImgCard, ListCard, StatCard } from '@educator/components'
import type { ListCardItem, StatCardItem } from '@educator/components'
import { Container } from '@educator/components/ProgramSections/Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '@educator/components/ProgramSections/i18n'
import { useTranslation } from '@mc/i18n'
import { SENIORITY_MIX_IMG_SRC } from './constants/previousCohortAssets'

interface IndustryCopy {
  label: string
}

interface StatCopy {
  label: string
  value: string
}

// ListCard renders exactly two columns and silently drops anything past
// itemsPerColumn * 2, so this has to stay half the industry count.
const INDUSTRIES_PER_COLUMN = 10

/**
 * The three previous-cohort cards, reusing the same ListCard/StatCard/ImgCard the
 * Status Portal renders. Only the copy and the background classes differ — the
 * cards take both as props, so the portal is unaffected.
 */
export const PreviousCohortSection = () => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)

  const rawIndustries = t('previousCohort.industriesCard.items', {
    returnObjects: true,
  })
  const industries: ListCardItem[] = (
    Array.isArray(rawIndustries) ? (rawIndustries as IndustryCopy[]) : []
  ).map((item, index) => ({ id: index, ...item }))

  const rawStats = t('previousCohort.statsCard.stats', { returnObjects: true })
  const stats: StatCardItem[] = (
    Array.isArray(rawStats) ? (rawStats as StatCopy[]) : []
  ).map((item, index) => ({ id: index, ...item }))

  return (
    <section
      id='previous-cohort'
      data-section='previous-cohort'
      className='w-full py-6 text-edu-fg-primary md:py-12'
    >
      <Container>
        <div className='flex flex-col gap-6 md:gap-4'>
          <header className='flex flex-col gap-3'>
            <div className='border-t border-edu-stroke-default' />
            <div className='flex items-center justify-between md:min-h-12'>
              <h2 className='m-0 text-xl leading-tight font-semibold md:text-title-3 md:leading-title-3'>
                {t('previousCohort.headline')}
              </h2>
            </div>
          </header>

          <div className='flex flex-col gap-4 lg:flex-row'>
            <div className='flex-1'>
              <ListCard
                title={t('previousCohort.industriesCard.title')}
                items={industries}
                itemsPerColumn={INDUSTRIES_PER_COLUMN}
                className='h-full bg-edu-brand-yellow'
              />
            </div>
            <div className='flex-1'>
              <StatCard
                title={t('previousCohort.statsCard.title')}
                stats={stats}
                className='h-full bg-edu-brand-green'
              />
            </div>
            <div className='flex-1'>
              <ImgCard
                title={t('previousCohort.imageCard.title')}
                src={SENIORITY_MIX_IMG_SRC}
                alt={t('previousCohort.imageCard.alt')}
                className='h-full bg-edu-accent-indigo-dark'
              />
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
